import { endOfWeek, startOfWeek } from "date-fns";
import { useMemo, useState } from "react";

import { GqlFieldworkerPreferredTransportationMethod } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";

import { AvailableWorkerData } from "../components/map-layers/WorkersLayerV2";
import usePlannerV2FieldworkerGetVisitDetails from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2FieldworkerGetVisitDetails";
import usePlannerV2FieldworkersGetPlannableForVisit from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2FieldworkersGetPlannableForVisit";

type FieldworkerBaseData = AvailableWorkerData;

export function usePlannerV2VisitFieldworkerData({
    onDate,
    plannerVisitId,
    timeslotId,
    filters,
}: {
    onDate: string;
    plannerVisitId: string;
    timeslotId?: string;
    filters: {
        transportationMethods?: GqlFieldworkerPreferredTransportationMethod[];
        fieldworkerIds?: string[];
        includeUnavailable?: boolean;
    };
}) {
    const {
        data: { availableFieldworkers },
        error,
        loading,
    } = usePlannerV2FieldworkersGetPlannableForVisit({
        onDate,
        plannerVisitId,
        timeslotId,
        ...filters,
    });

    // Create Map for lookups
    const availableFieldworkersById = useMemo(() => {
        if (!availableFieldworkers) return new Map();
        return new Map(availableFieldworkers.map(fieldworker => [fieldworker.id, fieldworker]));
    }, [availableFieldworkers]);

    const [hoveredFieldworkerBaseData, setHoveredFieldworkerBaseData] = useState<FieldworkerBaseData | undefined>(undefined);

    const { data: hoveredFieldworkerAdditionalData, loading: loadingHoveredFieldworkerAdditionalData } =
        usePlannerV2FieldworkerGetVisitDetails({
            plannerVisitId,
            availabilityStartAt: startOfWeek(parseStringToDate(onDate), { weekStartsOn: 1 }),
            availabilityEndAt: endOfWeek(parseStringToDate(onDate), { weekStartsOn: 1 }),
            fieldworkerId: hoveredFieldworkerBaseData?.id,
            userId: hoveredFieldworkerBaseData?.userId,
        });

    const handleFieldworkerHover = (workerId: string | undefined) => {
        if (workerId === hoveredFieldworkerBaseData?.id) {
            return;
        }

        const fieldworkerData = workerId ? availableFieldworkersById.get(workerId) : undefined;
        setHoveredFieldworkerBaseData(fieldworkerData);
    };

    return {
        error,
        loading,
        availableFieldworkers,
        availableFieldworkersById,
        hoveredFieldworkerBaseData,
        hoveredFieldworkerAdditionalData,
        loadingHoveredFieldworkerAdditionalData,
        handleFieldworkerHover,
        clearHoveredFieldworker: () => setHoveredFieldworkerBaseData(undefined),
    };
}
